import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {fileLevelCommentKey, type FileKey, type LineKey} from '@github-ui/diff-lines/types'
import {OUTSIDE_DIFF_KEY} from '../../helpers/diff-helpers'
import {useGetThreadFromThreadID, useMarkersActions} from '../../stores/selectors'
import type {ReviewMenuData} from '../payloads/review-menu'

export function useReviewMenuData(pendingThreadIds?: number[], options?: {enabled?: boolean}) {
  const enabled = options?.enabled ?? true
  const params = pendingThreadIds?.length ? new URLSearchParams() : undefined
  if (pendingThreadIds && params) {
    for (const id of pendingThreadIds) {
      params.append('threadIds[]', String(id))
    }
  }
  const reviewMenuURL = usePageDataUrl(PageData.reviewMenu, params?.toString())
  const getThreadFromThreadID = useGetThreadFromThreadID()
  const {updateThread} = useMarkersActions()

  return useQuery<ReviewMenuData>({
    queryKey: [PageData.reviewMenu, reviewMenuURL],
    queryFn: async () => {
      const result = await reactFetchJSON(reviewMenuURL)
      if (!result.ok) throw new Error(`HTTP ${result.status}: ${result.statusText}`)
      const json = (await result.json()) as ReviewMenuData

      if (json.threads) {
        seedThreads(json.threads, getThreadFromThreadID, updateThread)
      }

      return json
    },
    staleTime: Infinity,
    enabled,
  })
}

function seedThreads(
  threads: Thread[],
  getThreadFromThreadID: (threadID: number) => Thread | undefined,
  updateThread: (
    id: number,
    filePath: string,
    lineKey: LineKey | FileKey,
    transform: (old: Thread | undefined) => Thread | undefined,
  ) => void,
) {
  for (const thread of threads) {
    const threadId = Number(thread.id)
    if (getThreadFromThreadID(threadId)) continue

    if (thread.positioning) {
      const filePath = thread.positioning.path
      const isRightOrLeftSide = thread.positioning.endDiffSide === 'RIGHT' ? 'R' : 'L'
      const isFileLevelComment = thread.subjectType?.toUpperCase() === 'FILE'
      const lineKey = isFileLevelComment
        ? fileLevelCommentKey
        : thread.positioning.endLine
          ? (`${isRightOrLeftSide}${thread.positioning.endLine}` as LineKey)
          : OUTSIDE_DIFF_KEY

      updateThread(threadId, filePath, lineKey, () => thread)
    } else {
      updateThread(threadId, OUTSIDE_DIFF_KEY, OUTSIDE_DIFF_KEY, () => thread)
    }
  }
}
